import React, { useState, createContext, useContext } from 'react';
export type TaskPriority = 'Low' | 'Medium' | 'High' | 'Urgent';
export type TaskStatus = 'Open' | 'In Progress' | 'Complete' | 'Cancelled';
export interface Task {
  id: string;
  title: string;
  assignee: {
    name: string;
    avatar: string;
    email: string;
  };
  priority: TaskPriority;
  status: TaskStatus;
  dueDate: string;
  linkedTo: {
    id: string;
    type: 'contact' | 'subscription' | 'member';
    label?: string;
  } | null;
  createdBy: string;
  createdAt: string;
  completedAt?: string;
  completedBy?: string;
}
interface TaskContextType {
  tasks: Task[];
  addTask: (task: Omit<Task, 'id' | 'createdAt'>) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  getTasksByUser: (userName: string) => Task[];
  getTasksByLinkedEntity: (entityId: string) => Task[];
  getTaskActivityLogEvents: () => any[];
  getTaskNotifications: (userName: string) => any[];
}
const TaskContext = createContext<TaskContextType | undefined>(undefined);
const initialTasks: Task[] = [{
  id: '1',
  title: 'Call Ethan White about failed payment',
  assignee: {
    name: 'Sarah Chen',
    avatar: 'SC',
    email: 'sarah.chen@acmewine.com'
  },
  priority: 'Urgent',
  status: 'Open',
  dueDate: 'Feb 10 2026',
  linkedTo: {
    id: 'SUB-23130',
    type: 'subscription',
    label: 'Ethan White'
  },
  createdBy: 'Sarah Chen',
  createdAt: '2026-02-08T09:00:00'
}, {
  id: '2',
  title: "Follow up on Victoria Moore's card update",
  assignee: {
    name: 'Sarah Chen',
    avatar: 'SC',
    email: 'sarah.chen@acmewine.com'
  },
  priority: 'High',
  status: 'Open',
  dueDate: 'Feb 12 2026',
  linkedTo: {
    id: 'SUB-23121',
    type: 'subscription',
    label: 'Victoria Moore'
  },
  createdBy: 'Sarah Chen',
  createdAt: '2026-02-10T14:30:00'
}, {
  id: '3',
  title: 'Prepare Q1 club shipment wine selection',
  assignee: {
    name: 'Sarah Chen',
    avatar: 'SC',
    email: 'sarah.chen@acmewine.com'
  },
  priority: 'Medium',
  status: 'Open',
  dueDate: 'Feb 18 2026',
  linkedTo: null,
  createdBy: 'Mike Johnson',
  createdAt: '2026-02-09T11:15:00'
}, {
  id: '4',
  title: 'Review at-risk members for win-back campaign',
  assignee: {
    name: 'Sarah Chen',
    avatar: 'SC',
    email: 'sarah.chen@acmewine.com'
  },
  priority: 'Medium',
  status: 'Open',
  dueDate: 'Feb 20 2026',
  linkedTo: null,
  createdBy: 'Sarah Chen',
  createdAt: '2026-02-07T10:00:00'
}, {
  id: '5',
  title: 'Send welcome email to new Gold members',
  assignee: {
    name: 'Sarah Chen',
    avatar: 'SC',
    email: 'sarah.chen@acmewine.com'
  },
  priority: 'Low',
  status: 'Complete',
  dueDate: 'Feb 11 2026',
  linkedTo: null,
  createdBy: 'Sarah Chen',
  createdAt: '2026-02-05T16:45:00',
  completedAt: '2026-02-11T15:30:00',
  completedBy: 'Sarah Chen'
}, {
  id: '6',
  title: 'Confirm shipping address update',
  assignee: {
    name: 'Sarah Chen',
    avatar: 'SC',
    email: 'sarah.chen@acmewine.com'
  },
  priority: 'Medium',
  status: 'Complete',
  dueDate: 'Feb 11 2026',
  linkedTo: {
    id: '1',
    type: 'contact',
    label: 'Sarah Johnson'
  },
  createdBy: 'Mike Johnson',
  createdAt: '2026-02-08T13:20:00',
  completedAt: '2026-02-11T15:30:00',
  completedBy: 'Sarah Chen'
}, {
  id: '7',
  title: 'Update tasting room event pricing',
  assignee: {
    name: 'Chris Taylor',
    avatar: 'CT',
    email: 'chris.taylor@acmewine.com'
  },
  priority: 'Low',
  status: 'Complete',
  dueDate: 'Feb 11 2026',
  linkedTo: null,
  createdBy: 'Chris Taylor',
  createdAt: '2026-02-06T09:30:00',
  completedAt: '2026-02-11T11:00:00',
  completedBy: 'Chris Taylor'
}, {
  id: '8',
  title: 'Prepare April club newsletter',
  assignee: {
    name: 'Sarah Chen',
    avatar: 'SC',
    email: 'sarah.chen@acmewine.com'
  },
  priority: 'Low',
  status: 'Open',
  dueDate: 'Feb 19 2026',
  linkedTo: null,
  createdBy: 'Sarah Chen',
  createdAt: '2026-02-05T16:45:00'
}];
export function TaskProvider({
  children
}: {
  children: ReactNode;
}) {
  const [tasks, setTasks] = useState<Task[]>(initialTasks);
  const addTask = (taskData: Omit<Task, 'id' | 'createdAt'>) => {
    const newTask: Task = {
      ...taskData,
      id: `task-${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    setTasks(prev => [newTask, ...prev]);
  };
  const updateTask = (taskId: string, updates: Partial<Task>) => {
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        const updatedTask = {
          ...task,
          ...updates
        };
        // Handle completion logic
        if (updates.status === 'Complete' && task.status !== 'Complete' && !updates.completedAt) {
          updatedTask.completedAt = new Date().toISOString();
          // In a real app, we'd get the current user. Hardcoding Sarah Chen for now if not provided.
          updatedTask.completedBy = updates.completedBy || 'Sarah Chen';
        }
        return updatedTask;
      }
      return task;
    }));
  };
  const getTasksByUser = (userName: string) => {
    return tasks.filter(task => task.assignee.name === userName);
  };
  const getTasksByLinkedEntity = (entityId: string) => {
    return tasks.filter(task => task.linkedTo?.id === entityId);
  };
  // Helper to generate activity log events from tasks
  const getTaskActivityLogEvents = () => {
    const events: any[] = [];
    tasks.forEach(task => {
      // Creation event
      events.push({
        id: `task-create-${task.id}`,
        timestamp: task.createdAt,
        eventType: 'Task',
        activity: `Task created — ${task.title} (assigned to ${task.assignee.name}, due ${task.dueDate})`,
        entity: task.linkedTo ? task.linkedTo.label || task.linkedTo.id : '—',
        entityType: task.linkedTo ? task.linkedTo.type : null,
        source: 'Admin Portal',
        user: task.createdBy === 'Sarah Chen' ? 'sarah.chen@acmewine.com' : task.createdBy === 'Mike Johnson' ? 'mike.johnson@acmewine.com' : 'chris.taylor@acmewine.com',
        status: 'Success',
        changes: [{
          field: 'Task Status',
          before: '—',
          after: 'Open'
        }, {
          field: 'Assignee',
          before: '—',
          after: task.assignee.name
        }, {
          field: 'Priority',
          before: '—',
          after: task.priority
        }, {
          field: 'Due Date',
          before: '—',
          after: task.dueDate
        }]
      });
      // Completion event
      if (task.status === 'Complete' && task.completedAt) {
        events.push({
          id: `task-complete-${task.id}`,
          timestamp: task.completedAt,
          eventType: 'Task',
          activity: `Task completed — ${task.title} (completed by ${task.completedBy})`,
          entity: task.linkedTo ? task.linkedTo.label || task.linkedTo.id : '—',
          entityType: task.linkedTo ? task.linkedTo.type : null,
          source: 'Admin Portal',
          user: task.completedBy === 'Sarah Chen' ? 'sarah.chen@acmewine.com' : 'chris.taylor@acmewine.com',
          status: 'Success',
          changes: [{
            field: 'Task Status',
            before: 'Open',
            after: 'Complete'
          }, {
            field: 'Completed By',
            before: '—',
            after: task.completedBy
          }]
        });
      }
      // Overdue event (simulated for specific tasks based on requirements)
      // Task 1 is overdue
      if (task.id === '1') {
        events.push({
          id: `task-overdue-${task.id}`,
          timestamp: '2026-02-12T09:00:00',
          eventType: 'Task',
          activity: `Task overdue — ${task.title} (assigned to ${task.assignee.name}, 2 days overdue)`,
          entity: task.linkedTo ? task.linkedTo.label || task.linkedTo.id : '—',
          entityType: task.linkedTo ? task.linkedTo.type : null,
          source: 'System',
          user: 'Automated',
          status: 'Warning',
          changes: [{
            field: 'Task Status',
            before: 'Open',
            after: 'Overdue'
          }, {
            field: 'Days Overdue',
            before: '—',
            after: '2 days'
          }]
        });
      }
    });
    return events;
  };
  // Helper to generate notifications for a specific user
  const getTaskNotifications = (userName: string) => {
    const notifications: any[] = [];
    const today = new Date('2026-02-12'); // Hardcoded "today" based on context
    tasks.forEach(task => {
      // 1. Assigned to user (creation notification)
      if (task.assignee.name === userName && task.createdBy !== userName) {
        notifications.push({
          id: `notif-assign-${task.id}`,
          category: 'task',
          message: `${task.createdBy} assigned you a task: ${task.title}`,
          time: '2h ago',
          unread: true,
          taskPriority: task.priority,
          taskType: 'assigned',
          linkTo: '/tasks'
        });
      }
      // 2. Task completed (notify creator if different from completer)
      if (task.status === 'Complete' && task.createdBy === userName && task.completedBy !== userName) {
        notifications.push({
          id: `notif-complete-${task.id}`,
          category: 'task',
          message: `${task.completedBy} completed: ${task.title}`,
          time: 'Yesterday',
          unread: false,
          taskPriority: task.priority,
          taskType: 'completed',
          linkTo: '/tasks'
        });
      }
      // 3. Task overdue (for assignee)
      if (task.assignee.name === userName && task.status !== 'Complete' && task.status !== 'Cancelled') {
        const dueDate = new Date(task.dueDate);
        if (dueDate < today) {
          notifications.push({
            id: `notif-overdue-${task.id}`,
            category: 'task',
            message: `Task overdue: ${task.title}`,
            time: '1 day overdue',
            unread: true,
            taskPriority: task.priority,
            taskType: 'overdue',
            linkTo: '/tasks'
          });
        } else if (dueDate.getDate() === today.getDate() && dueDate.getMonth() === today.getMonth()) {
          notifications.push({
            id: `notif-due-${task.id}`,
            category: 'task',
            message: `Task due today: ${task.title}`,
            time: '8:00 AM',
            unread: true,
            taskPriority: task.priority,
            taskType: 'due',
            linkTo: '/tasks'
          });
        }
      }
    });
    return notifications;
  };
  return <TaskContext.Provider value={{
    tasks,
    addTask,
    updateTask,
    getTasksByUser,
    getTasksByLinkedEntity,
    getTaskActivityLogEvents,
    getTaskNotifications
  }} data-id="element-3955">
      {children}
    </TaskContext.Provider>;
}
export function useTasks() {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
}